/**
 * TableHistoryModal — past closed sessions for a single table.
 * Opens as a Dialog; fetches GET /tables/{id}/history on mount.
 */
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, History } from "lucide-react";
import { formatPrice } from "@/utils/currency";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
}

function methodLabel(m) {
  const MAP = {
    cash: "Dinheiro", dinheiro: "Dinheiro", efectivo: "Dinheiro",
    card: "Cartão", credit_card: "Cartão", mbway: "MBWay",
    bizum: "Bizum", pix: "Pix", stripe: "Stripe", viva: "Viva",
    revolut: "Revolut",
  };
  return MAP[(m || "").toLowerCase()] || m || "—";
}

function SessionRow({ session, table, onPaymentClick }) {
  const [open, setOpen] = useState(false);
  const allItems = session.orders.flatMap(o =>
    o.status !== "cancelled" ? o.items.map(it => ({ ...it, order_id: o.id })) : []
  );

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition text-left"
        onClick={() => setOpen(v => !v)}
      >
        {open ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-gray-800">
              {fmtDate(session.opened_at)}
            </span>
            <span className="text-xs text-gray-400">
              {fmtTime(session.opened_at)}
              {session.closed_at !== session.opened_at && ` – ${fmtTime(session.closed_at)}`}
            </span>
            {session.cancelled_count > 0 && (
              <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">
                {session.cancelled_count} cancel.
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {session.payment_methods.map(m => (
              <Badge key={m} className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                {methodLabel(m)}
              </Badge>
            ))}
            {!session.all_paid && (
              <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px]">Não pago</Badge>
            )}
          </div>
        </div>
        <span className="font-bold text-emerald-700 text-sm shrink-0">
          {formatPrice(session.total)}
        </span>
      </button>

      {open && (
        <div className="border-t bg-gray-50 px-4 py-3">
          {allItems.length === 0 ? (
            <p className="text-xs text-gray-400 italic">Sem itens registados</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 uppercase tracking-wide">
                  <th className="text-left pb-1 font-medium">Item</th>
                  <th className="text-center pb-1 font-medium w-8">Qtd</th>
                  <th className="text-right pb-1 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {allItems.map((it, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="py-1 text-gray-700">{it.name || "—"}</td>
                    <td className="py-1 text-center text-gray-500">{it.qty}</td>
                    <td className="py-1 text-right text-gray-700 font-medium">
                      {formatPrice(it.qty * it.price, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200">
                  <td colSpan={2} className="pt-2 text-gray-500 font-medium">Total</td>
                  <td className="pt-2 text-right font-bold text-emerald-700">
                    {formatPrice(session.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}

          {/* Payment lines if closed account */}
          {session.payments && session.payments.length > 0 && (
            <div className="mt-3 pt-3 border-t text-xs">
              <p className="font-semibold text-gray-700 mb-2">Pagamentos</p>
              <div className="space-y-1">
                {session.payments.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => onPaymentClick(p, session.orders)}
                    className="w-full text-left p-2 hover:bg-emerald-50 rounded border border-emerald-100 transition"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">
                        {methodLabel(p.method)} • {fmtTime(p.created_at)}
                      </span>
                      <span className="font-semibold text-emerald-700">
                        {formatPrice(p.amount)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function TableHistoryModal({ table, open, onClose }) {
  const { authAxios } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const { ClosedAccountModal } = require("./ClosedAccountModal");

  const tableName = table?.name || `MESA ${String(table?.number ?? "").padStart(2, "0")}`;

  useEffect(() => {
    if (!open || !table?.id) return;
    setLoading(true);
    authAxios.get(`/tables/${table.id}/history`)
      .then(r => setSessions(r.data || []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [open, table?.id, authAxios]);

  const handlePaymentClick = (payment, orders) => {
    setSelectedPayment(payment);
    setSelectedOrders(orders);
    setPaymentModalOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-600" />
              Histórico — {tableName}
            </DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="py-8 text-center text-gray-400 text-sm">A carregar...</div>
          )}

          {!loading && sessions.length === 0 && (
            <div className="py-8 text-center text-gray-400">
              <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sem sessões anteriores registadas</p>
            </div>
          )}

          {!loading && sessions.length > 0 && (
            <div className="space-y-2 mt-2">
              {sessions.map(s => (
                <SessionRow
                  key={s.session_id}
                  session={s}
                  table={table}
                  onPaymentClick={handlePaymentClick}
                />
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedPayment && (
        <ClosedAccountModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          payment={selectedPayment}
          orders={selectedOrders}
          tableId={table?.id}
        />
      )}
    </>
  );
}
